import React, { useState, useContext, useEffect } from 'react';
import {
  Nav,
  Offcanvas,
  Navbar,
  Container,
  Button,
} from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  BiGridAlt,
  BiUser,
  BiCalendar,
  BiTransfer,
  BiGroup,
  BiChevronDown,
  BiChevronRight,
  BiLogOut,
  BiWallet,
  BiCog,
  BiPrinter,
  BiMoney,
} from 'react-icons/bi';

export default function Sidebar() {
  const { user, logout, hasRight } = useContext(AuthContext);
  const location = useLocation();

  const [show, setShow] = useState(false);

  // Une seule section repliable peut être ouverte à la fois.
  const [openSection, setOpenSection] = useState(null);

  const close = () => setShow(false);
  const open = () => setShow(true);

  const canSeeCashOpenings =
    hasRight('CASH_MANAGEMENT_VIEW') ||
    hasRight('CASH_OPEN') ||
    hasRight('CASH_APPORT') ||
    hasRight('CASH_WITHDRAW') ||
    hasRight('CASH_CLOSE') ||
    hasRight('CASH_APPROVE_OPEN') ||
    hasRight('CASH_APPROVE_APPORT') ||
    hasRight('CASH_APPROVE_WITHDRAW') ||
    hasRight('CASH_APPROVE_CLOSE');

  const canSeeCashSession =
    hasRight('CASH_VIEW') || hasRight('CASH_HISTORY_VIEW');

  const canSeeServices = [
    hasRight('TIKANE_VIEW') || hasRight('TIKANE_CREATE'),
    hasRight('TIKANE_SENP_VIEW') || hasRight('TIKANE_SENP_CREATE'),
    hasRight('ZELLE_VIEW') || hasRight('ZELLE_CREATE'),
    hasRight('MONCASH_VIEW') || hasRight('MONCASH_CREATE'),
    hasRight('NATCASH_VIEW') || hasRight('NATCASH_CREATE'),
    hasRight('EXCHANGE_VIEW') || hasRight('EXCHANGE_CREATE'),
    hasRight('MICRO_CREDIT_VIEW') || hasRight('MICRO_CREDIT_CREATE'),
  ].some(Boolean);

  const canSeeClientSection = [
    hasRight('CLIENT_VIEW'),
    hasRight('CLIENT_CREATE'),
    hasRight('CLIENT_UPDATE'),
    hasRight('CLIENT_DELETE'),
    hasRight('CLIENT_MINOR_VIEW'),
    hasRight('CLIENT_MINOR_CREATE'),
    hasRight('CLIENT_ACTIVATE'),
    hasRight('CLIENT_DEACTIVATE'),
  ].some(Boolean);

  const canSeeFeesSection = [
    hasRight('MONCASH_FEE_VIEW'),
    hasRight('MONCASH_FEE_UPDATE'),
    hasRight('NATCASH_FEE_VIEW'),
    hasRight('NATCASH_FEE_UPDATE'),
    hasRight('RATE_VIEW'),
    hasRight('RATE_UPDATE'),
  ].some(Boolean);

  const canSeeUsersSection = [
    hasRight('USER_VIEW'),
    hasRight('USER_CREATE'),
    hasRight('USER_UPDATE'),
    hasRight('USER_DELETE'),
  ].some(Boolean);

  const canSeePrinterSection =
    hasRight('PRINT_SETTINGS_VIEW') ||
    hasRight('PRINT_SETTINGS_UPDATE') ||
    hasRight('PRINT_DOWNLOAD');

  const isActiveIn = (paths) =>
    paths.some((p) => location.pathname.startsWith(p));

  const servicesActive = isActiveIn([
    '/tikane',
    '/tikanesenp',
    '/zelle',
    '/moncash',
    '/natcash',
    '/exchange',
    '/microcredit',
  ]);

  const clientSectionActive = isActiveIn(['/clients']);
  const feesActive = isActiveIn(['/moncash-fees', '/natcash-fees', '/daily-rate']);
  const usersActive = isActiveIn(['/users']);
  const printerActive = isActiveIn(['/print-settings']);

  // Ouvrir automatiquement une seule section correspondant à la route courante.
  // Les sections les plus spécifiques sont testées avant "Nos Services"
  // afin d'éviter que /moncash-fees ouvre aussi /moncash.
  useEffect(() => {
    if (feesActive) {
      setOpenSection('fees');
      return;
    }
    if (clientSectionActive) {
      setOpenSection('clients');
      return;
    }
    if (usersActive) {
      setOpenSection('employees');
      return;
    }
    if (printerActive) {
      setOpenSection('printer');
      return;
    }
    if (servicesActive) {
      setOpenSection('services');
      return;
    }
    setOpenSection(null);
  }, [servicesActive, clientSectionActive, feesActive, usersActive, printerActive]);

  const sectionButtonClass = (active) =>
    `d-flex w-100 align-items-center justify-content-between px-3 py-2 mb-2 rounded border-0 ${
      active ? 'bg-gold text-dark' : 'text-white'
    }`;

  const linkClass = ({ isActive }) =>
    `d-flex align-items-center mb-2 px-3 py-2 rounded text-decoration-none ${
      isActive ? 'bg-gold text-dark' : 'text-white'
    }`;

  const PlainLink = ({ to, icon, label }) => (
    <NavLink to={to} onClick={close} className={linkClass}>
      <span className="me-2">{icon}</span>
      {label}
    </NavLink>
  );

  const Section = ({
    sectionKey,
    label,
    icon,
    active,
    children,
  }) => {
    const isOpen = openSection === sectionKey;

    return (
      <>
        <button
          className={sectionButtonClass(active)}
          style={{ background: 'transparent' }}
          onClick={() => setOpenSection((current) => (current === sectionKey ? null : sectionKey))}
          aria-expanded={isOpen}
        >
          <span className="fw-semibold d-flex align-items-center">
            <span className="me-2">{icon}</span>
            {label}
          </span>
          {isOpen ? <BiChevronDown size={18} /> : <BiChevronRight size={18} />}
        </button>
        {isOpen && <div className="ms-2">{children}</div>}
      </>
    );
  };

  return (
    <>
      <Navbar bg="primary" variant="dark">
        <Container fluid>
          <Button variant="outline-light" onClick={open}>
            <span className="navbar-toggler-icon" />
          </Button>
          <Navbar.Brand className="ms-2 text-white">
            Bienvenue, {user?.username} !
          </Navbar.Brand>
        </Container>
      </Navbar>

      <Offcanvas show={show} onHide={close} className="bg-primary text-white">
        <Offcanvas.Header closeButton closeVariant="white">
          <Offcanvas.Title>Crystal Multi Services</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <Nav className="flex-column">
            <PlainLink
              to="/dashboard"
              icon={<BiGridAlt size={18} />}
              label="Dashboard"
            />

            {canSeeCashSession && (
              <PlainLink
                to="/cash-sessions"
                icon={<BiWallet size={18} />}
                label="Gestion et rapport de ma caisse"
              />
            )}

            {canSeeServices && (
              <Section
                sectionKey="services"
                label="Nos Services"
                icon={<BiTransfer size={18} />}
                active={servicesActive}
              >
                {(hasRight('TIKANE_VIEW') || hasRight('TIKANE_CREATE')) && (
                  <PlainLink
                    to="/tikane"
                    icon={<BiCalendar size={18} />}
                    label="Ti kanè"
                  />
                )}

                {(hasRight('TIKANE_SENP_VIEW') || hasRight('TIKANE_SENP_CREATE')) && (
                  <PlainLink
                    to="/tikanesenp"
                    icon={<BiCalendar size={18} />}
                    label="Kanè senp"
                  />
                )}

                {(hasRight('ZELLE_VIEW') || hasRight('ZELLE_CREATE')) && (
                  <PlainLink
                    to="/zelle"
                    icon={<BiTransfer size={18} />}
                    label="Zelle"
                  />
                )}

                {(hasRight('MONCASH_VIEW') || hasRight('MONCASH_CREATE')) && (
                  <PlainLink
                    to="/moncash"
                    icon={<BiTransfer size={18} />}
                    label="Moncash"
                  />
                )}

                {(hasRight('NATCASH_VIEW') || hasRight('NATCASH_CREATE')) && (
                  <PlainLink
                    to="/natcash"
                    icon={<BiTransfer size={18} />}
                    label="Natcash"
                  />
                )}

                {(hasRight('EXCHANGE_VIEW') || hasRight('EXCHANGE_CREATE')) && (
                  <PlainLink
                    to="/exchange"
                    icon={<BiMoney size={18} />}
                    label="Exchange system"
                  />
                )}

                {(hasRight('MICRO_CREDIT_VIEW') || hasRight('MICRO_CREDIT_CREATE')) && (
                  <PlainLink
                    to="/microcredit"
                    icon={<BiTransfer size={18} />}
                    label="Microcredit"
                  />
                )}
              </Section>
            )}

            {canSeeClientSection && (
              <Section
                sectionKey="clients"
                label="Services client"
                icon={<BiUser size={18} />}
                active={clientSectionActive}
              >
                <PlainLink
                  to="/clients"
                  icon={<BiUser size={18} />}
                  label="Client"
                />
              </Section>
            )}

            {canSeeCashOpenings && (
              <PlainLink
                to="/cash-openings"
                icon={<BiWallet size={18} />}
                label="Gestion et rapport des caisses"
              />
            )}

            {canSeeFeesSection && (
              <Section
                sectionKey="fees"
                label="Frais et taux du jour"
                icon={<BiCog size={18} />}
                active={feesActive}
              >
                {(hasRight('MONCASH_FEE_VIEW') || hasRight('MONCASH_FEE_UPDATE')) && (
                  <PlainLink
                    to="/moncash-fees"
                    icon={<BiCog size={18} />}
                    label="Frais Moncash"
                  />
                )}

                {(hasRight('NATCASH_FEE_VIEW') || hasRight('NATCASH_FEE_UPDATE')) && (
                  <PlainLink
                    to="/natcash-fees"
                    icon={<BiCog size={18} />}
                    label="Frais Natcash"
                  />
                )}

                {(hasRight('RATE_VIEW') || hasRight('RATE_UPDATE')) && (
                  <PlainLink
                    to="/daily-rate"
                    icon={<BiCalendar size={18} />}
                    label="Taux du jour"
                  />
                )}
              </Section>
            )}

            {canSeeUsersSection && (
              <Section
                sectionKey="employees"
                label="Employés"
                icon={<BiGroup size={18} />}
                active={usersActive}
              >
                <PlainLink
                  to="/users"
                  icon={<BiGroup size={18} />}
                  label="Utilisateurs"
                />
              </Section>
            )}

            {canSeePrinterSection && (
              <Section
                sectionKey="printer"
                label="Paramètre imprimante et impression"
                icon={<BiPrinter size={18} />}
                active={printerActive}
              >
                <PlainLink
                  to="/print-settings"
                  icon={<BiPrinter size={18} />}
                  label="Paramètres impression"
                />
              </Section>
            )}

            <div className="mt-4">
              <Nav.Link
                onClick={() => {
                  logout();
                  close();
                }}
                className="d-flex align-items-center px-3 py-2 text-white"
              >
                <BiLogOut size={20} className="me-2" />
                Déconnexion
              </Nav.Link>
            </div>
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
